// Date/time helpers for the Planner. All date strings are 'YYYY-MM-DD' in the
// user's LOCAL timezone; times are 'HH:MM'. Period-key logic mirrors the backend
// (weekly key = Sunday date — weeks start on Sunday, monthly key = 'YYYY-MM').

export const pad = (n) => String(n).padStart(2, '0');

export function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
export function fmtISO(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
export function parseISO(s) {
  const [y, m, d] = (s || todayStr()).split('-').map(Number);
  return new Date(y, m - 1, d);
}
export function addDays(s, n) { const d = parseISO(s); d.setDate(d.getDate() + n); return fmtISO(d); }
export function addWeeks(s, n) { return addDays(s, n * 7); }
export function addMonths(s, n) { const d = parseISO(s); d.setMonth(d.getMonth() + n); return fmtISO(d); }
export function weekStart(s) {
  const d = parseISO(s);
  const dow = d.getDay(); // Sun=0 .. Sat=6 — weeks start on Sunday
  d.setDate(d.getDate() - dow);
  return fmtISO(d);
}
export function tzOffsetMin() { return -new Date().getTimezoneOffset(); }

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DOW_HEADERS = DOW_SHORT;

export function weekdayIndex(s) { return parseISO(s).getDay(); } // Sun=0 .. Sat=6
export function dowShort(s) { return DOW_SHORT[weekdayIndex(s)]; }
export function dowLong(s) { return DOW_LONG[weekdayIndex(s)]; }
export function dayNum(s) { return parseISO(s).getDate(); }
export function prettyDate(s) { const d = parseISO(s); return `${DOW_SHORT[d.getDay()]}, ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`; }
export function prettyDateLong(s) { const d = parseISO(s); return `${DOW_LONG[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`; }
export function monthLabel(s) { const d = parseISO(s); return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`; }
export function weekRangeLabel(ws) {
  const we = addDays(ws, 6); const a = parseISO(ws); const b = parseISO(we);
  const left = `${a.getDate()} ${MONTHS_SHORT[a.getMonth()]}`;
  const right = `${b.getDate()} ${MONTHS_SHORT[b.getMonth()]}${b.getFullYear() !== a.getFullYear() ? ' ' + b.getFullYear() : ''}`;
  return `${left} \u2013 ${right}`;
}

export function periodKey(type, s) {
  if (type === 'weekly') return weekStart(s);
  if (type === 'monthly') return s.slice(0, 7);
  return s;
}

// ---- time ----
export const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) { for (const m of [0, 15, 30, 45]) { out.push(`${pad(h)}:${pad(m)}`); } }
  return out;
})();
export function timeToMin(t) { if (!t) return null; const [h, m] = t.split(':').map(Number); return h * 60 + m; }

// Add minutes to an 'HH:MM' time, clamped within the same day (max 23:59 so an
// auto-computed end is always strictly after a late start).
export function addMinutesToTime(t, mins) {
  const base = timeToMin(t);
  if (base === null || isNaN(base)) return null;
  let total = base + (Number(mins) || 0);
  if (total > 23 * 60 + 59) total = 23 * 60 + 59;
  if (total < 0) total = 0;
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

// Parse a loosely-typed time into canonical 'HH:MM' (24h), or null if invalid.
// Accepts: "16:00", "16", "1600", "160", "4:30", "4", "4pm", "4:30 pm", "12am".
// Empty string returns '' (caller treats as "no time").
export function normalizeTime(raw) {
  if (raw == null) return null;
  let s = String(raw).trim().toLowerCase();
  if (!s) return '';
  let ampm = null;
  const ap = s.match(/([ap])\.?\s*m?\.?$/);
  if (ap && /[ap]/.test(s.slice(-3))) { ampm = ap[1]; s = s.replace(/\s*[ap]\.?\s*m?\.?$/, '').trim(); }
  let hh, mm;
  if (s.includes(':')) {
    const [a, b] = s.split(':');
    hh = parseInt(a, 10); mm = parseInt(b, 10);
  } else if (/^\d+$/.test(s)) {
    if (s.length <= 2) { hh = parseInt(s, 10); mm = 0; }
    else if (s.length === 3) { hh = parseInt(s.slice(0, 1), 10); mm = parseInt(s.slice(1), 10); }
    else { hh = parseInt(s.slice(0, 2), 10); mm = parseInt(s.slice(2, 4), 10); }
  } else {
    return null;
  }
  if (isNaN(hh) || isNaN(mm)) return null;
  if (ampm) {
    if (hh < 1 || hh > 12) return null;
    if (ampm === 'p' && hh !== 12) hh += 12;
    if (ampm === 'a' && hh === 12) hh = 0;
  }
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return `${pad(hh)}:${pad(mm)}`;
}
export function fmtTimeRange(a, b) {
  if (!a) return 'All day';
  return b ? `${a} \u2013 ${b}` : a;
}

export function missedLabel(goal) {
  if (!goal) return null;
  if (goal.completed) return null;
  if (goal.expired) return 'Expired';
  if (goal.missed_count > 0) return `Missed for ${goal.missed_count} ${goal.unit}${goal.missed_count > 1 ? 's' : ''}`;
  return null;
}

// ---- recurrence ----
export const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly (same date)' },
  { value: 'monthly_weekday', label: 'Monthly (same weekday)' },
  { value: 'yearly', label: 'Yearly' },
];

const ORD_WORD = ['', 'first', 'second', 'third', 'fourth', 'fifth'];

export function weekdayOrdinalInfo(s) {
  const d = parseISO(s);
  const day = d.getDate();
  const ordinal = Math.floor((day - 1) / 7) + 1;
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const isLast = day + 7 > lastDay;
  return { ordinal, isLast };
}

// Human description of a recurrence rule given its anchor date.
export function recurrenceLabel(rec, s) {
  if (!rec || rec === 'none' || !s) return '';
  const d = parseISO(s);
  const dowIdx = d.getDay();
  if (rec === 'daily') return 'Every day';
  if (rec === 'weekly') return `Every ${DOW_LONG[dowIdx]}`;
  if (rec === 'monthly') return `Monthly on day ${d.getDate()}`;
  if (rec === 'yearly') return `Every year on ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
  if (rec === 'monthly_weekday') {
    const { ordinal, isLast } = weekdayOrdinalInfo(s);
    const word = isLast ? 'last' : ORD_WORD[ordinal];
    return `Monthly on the ${word} ${DOW_LONG[dowIdx]}`;
  }
  return '';
}

// Short tag for chips (e.g. a recurring badge tooltip fallback).
export function recurrenceShort(rec) {
  switch (rec) {
    case 'daily': return 'Daily';
    case 'weekly': return 'Weekly';
    case 'monthly': return 'Monthly';
    case 'monthly_weekday': return 'Monthly';
    case 'yearly': return 'Yearly';
    default: return '';
  }
}
